import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  FormControl,
  FormLabel,
  Textarea,
  Box,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { FC, useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { FaSyncAlt } from 'react-icons/fa'

import TableGrid from '@component/Layout/TableGrid'

import { parsePurchaseMrpData } from '@helper/grid'
import {
  formatDate,
  getDesiredQuantity,
  getItensComFalha,
  getRecommendedQuantity,
  getShortPedidoId,
  isPendente,
  isScheduledPurchase,
  parsePtBrNumber,
  parsePurchaseMrpScheduledData,
  withDesiredQuantity
} from '@helper/mrp/approval'
import mrp from '@service/mrp'

import { IAlteredSku } from '@base/@types/components/Modules/Mrp/Approval/VisualizarModal'
import {
  IApprovalFormData,
  IMrpRecomendacaoItem,
  IPendingOrderGroup
} from '@base/@types/providers/Mrp/ApprovalProvider'
import mrpPurchaseHeader from '@base/variables/headers/mrp/purchase'
import mrpScheduledPurchaseHeader from '@base/variables/headers/mrp/scheduled-purchase'

import FalhasAlert from './FalhasAlert'
import GroupTable from './GroupTable'

interface VisualizarModalProps {
  isOpen: boolean
  onClose: () => void
  onApprove?: (data: IApprovalFormData) => Promise<boolean | void>
  group: IPendingOrderGroup | null
  showApprovalForm?: boolean
}

interface UpdateWantedOptions {
  // Reversão em lote: sem toast por item (o lote mostra um toast de progresso)
  silent?: boolean
}

const BATCH_DELAY_MS = 500

const VisualizarModal: FC<VisualizarModalProps> = ({
  isOpen,
  onClose,
  onApprove,
  group,
  showApprovalForm = true
}) => {
  const toast = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mrpData, setMrpData] = useState<any[]>([])
  // Dados originais (sem parsing): base do PUT de quantidade e de quantidadeAjustadas
  const [originalMrpData, setOriginalMrpDataState] = useState<
    IMrpRecomendacaoItem[]
  >([])
  const [loadingMrp, setLoadingMrp] = useState(false)
  const [alteredSkus, setAlteredSkus] = useState<IAlteredSku[]>([])
  const [observacao, setObservacao] = useState<string>('')
  const [isBatchReverting, setIsBatchReverting] = useState(false)
  const [cienteDosFalhos, setCienteDosFalhos] = useState(false)
  // Remonta a grid para descartar o texto digitado na célula quando a edição falha
  const [tableKey, setTableKey] = useState(0)
  const revertConfirm = useDisclosure()
  const revertCancelRef = useRef<HTMLButtonElement>(null)

  // Leituras síncronas dentro de handlers assíncronos (lote, respostas da API)
  const originalMrpDataRef = useRef<IMrpRecomendacaoItem[]>([])
  // Quantidade desejada de cada item quando o pedido foi carregado
  const initialQuantitiesRef = useRef<Map<number, number>>(new Map())
  // Pedido exibido no momento (null com o modal fechado): descarta respostas e
  // interrompe o lote quando o modal fecha ou troca de pedido
  const activeSapIdRef = useRef<string | null>(null)
  activeSapIdRef.current = isOpen ? group?.idRequestSap ?? null : null

  const isScheduled = isScheduledPurchase(group?.tipoProcesso)
  const podeEditar = isPendente(group?.aprovado)

  const setOriginalMrpData = useCallback((data: IMrpRecomendacaoItem[]) => {
    originalMrpDataRef.current = data
    setOriginalMrpDataState(data)
  }, [])

  const parseMrpRows = useCallback(
    (data: IMrpRecomendacaoItem[]) =>
      isScheduled
        ? parsePurchaseMrpScheduledData(data)
        : parsePurchaseMrpData(data),
    [isScheduled]
  )

  const itensFalhos = useMemo(() => getItensComFalha(group?.items), [group])
  const temFalhas = itensFalhos.length > 0

  const resetState = useCallback(() => {
    setAlteredSkus([])
    setObservacao('')
    setCienteDosFalhos(false)
  }, [])

  // Troca de pedido ou abertura/fechamento: não deixa o estado vazar
  useEffect(() => {
    resetState()
  }, [isOpen, group?.idRequestSap, resetState])

  useEffect(() => {
    const sapId = group?.idRequestSap
    if (!isOpen || !sapId) return

    let cancelled = false
    setOriginalMrpData([])
    setMrpData([])
    initialQuantitiesRef.current = new Map()
    setLoadingMrp(true)

    mrp
      .getMrpDetailsById(sapId)
      .then(({ data }) => {
        if (cancelled) return
        const raw: IMrpRecomendacaoItem[] = Array.isArray(data?.data)
          ? data.data
          : []

        initialQuantitiesRef.current = new Map(
          raw.map(item => [
            item?.estoque?.id,
            getDesiredQuantity(item, isScheduled) ?? 0
          ])
        )
        setOriginalMrpData(raw)
        setMrpData(parseMrpRows(raw))
      })
      .catch(() => {
        if (cancelled) return
        setOriginalMrpData([])
        setMrpData([])
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar os dados MRP',
          status: 'error',
          duration: 5000,
          isClosable: true
        })
      })
      .finally(() => {
        if (!cancelled) setLoadingMrp(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    isOpen,
    group?.idRequestSap,
    isScheduled,
    parseMrpRows,
    setOriginalMrpData,
    toast
  ])

  // Substitui (imutável) o item bruto pelo retornado pelo servidor e re-parseia a
  // linha, mantendo prefixo T/F e formatação pt-BR da quantidade desejada
  const applyServerItem = (
    estoqueId: number,
    serverItem: IMrpRecomendacaoItem
  ) => {
    setOriginalMrpData(
      originalMrpDataRef.current.map(item =>
        item?.estoque?.id === estoqueId ? serverItem : item
      )
    )
    const [parsedRow] = parseMrpRows([serverItem])
    setMrpData(prev =>
      prev.map(row => (row['estoque-id'] === estoqueId ? parsedRow : row))
    )
  }

  const upsertAlteredSku = (
    estoqueId: number,
    serverItem: IMrpRecomendacaoItem,
    novaQuantidade: number
  ) => {
    const quantidadeOriginal = initialQuantitiesRef.current.get(estoqueId) ?? 0
    const custoUnitario =
      Number(serverItem?.estoque?.custoUnitarioFatorConversao) || 0
    const valor = novaQuantidade * custoUnitario

    setAlteredSkus(prev => {
      const existing = prev.find(sku => sku.id === estoqueId)

      // Voltou ao valor original: deixa de ser um item ajustado
      if (novaQuantidade === quantidadeOriginal) {
        return existing ? prev.filter(sku => sku.id !== estoqueId) : prev
      }

      if (existing) {
        return prev.map(sku =>
          sku.id === estoqueId ? { ...sku, novaQuantidade, valor } : sku
        )
      }

      return [
        ...prev,
        {
          id: estoqueId,
          loja: serverItem?.estoque?.loja || null,
          produto: serverItem?.estoque?.produtoFornecedor?.produto || null,
          idEmpresa: serverItem?.estoque?.idEmpresa || 0,
          quantidadeOriginal,
          novaQuantidade,
          justificativa: '',
          observacao: '',
          acimaToleranciaPermitida: !!serverItem?.acimaToleranciaPermitida,
          valor
        }
      ]
    })
  }

  // Retorna true quando a quantidade ficou salva (ou não mudou)
  const handleUpdateMrpWanted = async (
    estoqueId: number,
    wanted: number | string,
    { silent = false }: UpdateWantedOptions = {}
  ): Promise<boolean> => {
    const sapId = activeSapIdRef.current
    const newQuantity = parsePtBrNumber(wanted)

    if (!Number.isFinite(newQuantity) || newQuantity < 0) {
      setTableKey(key => key + 1)
      if (!silent) {
        toast({
          title: 'Erro',
          description: 'Quantidade deve ser um número válido e não negativo',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
      return false
    }

    const currentItem = originalMrpDataRef.current.find(
      item => item?.estoque?.id === estoqueId
    )
    if (!currentItem) {
      if (!silent) {
        toast({
          title: 'Erro',
          description: 'Item não encontrado nos dados do pedido',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
      return false
    }

    // Blur sem alteração: nada a enviar
    if (newQuantity === getDesiredQuantity(currentItem, isScheduled)) {
      return true
    }

    // O PurchaseProvider envia o item inteiro com a nova quantidade
    const purchaseItem = withDesiredQuantity(
      currentItem,
      newQuantity,
      isScheduled
    )

    try {
      const {
        data: { data: updatedSkuData }
      } = await mrp.putPurshaseWanted({
        page: 1,
        params: {
          quantidadeDesejada: String(newQuantity).replace(/\.|-/g, ''),
          recomendacaoFornecimentoCommand: purchaseItem
        }
      })

      // Modal fechado ou outro pedido aberto durante a requisição
      if (activeSapIdRef.current !== sapId) return false

      const serverItem: IMrpRecomendacaoItem =
        updatedSkuData?.estoque?.id === estoqueId
          ? updatedSkuData
          : purchaseItem
      const finalQuantity =
        getDesiredQuantity(serverItem, isScheduled) ?? newQuantity

      applyServerItem(estoqueId, serverItem)
      upsertAlteredSku(estoqueId, serverItem, finalQuantity)

      if (!silent) {
        toast({
          title: 'Quantidade atualizada',
          description: `Quantidade desejada alterada para ${finalQuantity.toLocaleString(
            'pt-BR'
          )}`,
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      }
      return true
    } catch (error: any) {
      if (activeSapIdRef.current !== sapId) return false

      const status = error?.response?.status
      // 400 pode trazer o item corrigido pelo servidor (ex.: arredondado ao lote)
      const serverItem: IMrpRecomendacaoItem | undefined =
        status === 400 ? error?.response?.data?.data : undefined

      if (serverItem?.estoque?.id === estoqueId) {
        applyServerItem(estoqueId, serverItem)
        upsertAlteredSku(
          estoqueId,
          serverItem,
          getDesiredQuantity(serverItem, isScheduled) ?? 0
        )
      }
      // Descarta o texto digitado (o state não foi alterado)
      setTableKey(key => key + 1)

      if (!silent) {
        toast({
          title: 'Atenção',
          description:
            status === 400
              ? error?.response?.data?.message ||
                'Não foi possível atualizar a quantidade'
              : 'Ocorreu um problema interno. Tente novamente mais tarde',
          status: status === 400 ? 'warning' : 'error',
          duration: 3000,
          isClosable: true
        })
      }
      return false
    }
  }

  const itensParaReverter = () =>
    originalMrpDataRef.current.filter(item => {
      const recomendada = getRecommendedQuantity(item, isScheduled) ?? 0
      return recomendada !== getDesiredQuantity(item, isScheduled)
    })

  const handleRequestBatchRevert = () => {
    if (isBatchReverting) return
    if (itensParaReverter().length === 0) {
      toast({
        title: 'Nada a reverter',
        description: 'Todos os itens já estão na quantidade recomendada',
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }
    revertConfirm.onOpen()
  }

  const handleBatchRevertToOriginal = async () => {
    revertConfirm.onClose()
    const sapId = activeSapIdRef.current
    const itens = itensParaReverter()
    if (!sapId || isBatchReverting || itens.length === 0) return

    setIsBatchReverting(true)
    let successCount = 0
    let errorCount = 0
    let aborted = false

    const progressToast = toast({
      title: 'Revertendo para a recomendação original',
      description: `0/${itens.length} itens processados`,
      status: 'info',
      duration: null,
      isClosable: false
    })

    try {
      for (let i = 0; i < itens.length; i++) {
        if (activeSapIdRef.current !== sapId) {
          aborted = true
          break
        }

        const item = itens[i]
        const ok = await handleUpdateMrpWanted(
          item.estoque.id,
          getRecommendedQuantity(item, isScheduled) ?? 0,
          { silent: true }
        )
        if (ok) successCount++
        else errorCount++

        if (progressToast) {
          toast.update(progressToast, {
            description: `${i + 1}/${itens.length} itens processados`
          })
        }

        // Intervalo entre requisições para não sobrecarregar o backend
        if (i < itens.length - 1) {
          await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS))
        }
      }
    } finally {
      if (progressToast) toast.close(progressToast)
      setIsBatchReverting(false)
    }

    if (aborted) return

    toast({
      title: 'Reversão em lote concluída',
      description: `${successCount} itens revertidos com sucesso${
        errorCount > 0 ? `, ${errorCount} com erro` : ''
      }`,
      status: errorCount > 0 ? 'warning' : 'success',
      duration: 5000,
      isClosable: true
    })
  }

  const handleApproval = async (aprovar: boolean) => {
    if (!onApprove) return

    const alteredIds = new Set(alteredSkus.map(sku => sku.id))
    const data: IApprovalFormData = {
      aprovar,
      // Valor esperado pelo backend: 'Aprovado' | 'Recusado'
      justificativa: aprovar ? 'Aprovado' : 'Recusado',
      observacao,
      enviadoComFalha: temFalhas && cienteDosFalhos
    }

    if (alteredIds.size > 0) {
      data.quantidadeAjustadas = originalMrpData.filter(item =>
        alteredIds.has(item?.estoque?.id)
      )
    }

    setIsSubmitting(true)

    try {
      const result = await onApprove(data)
      // No erro (false) o modal fica aberto com os ajustes; o toast explica o motivo
      if (result !== false) {
        resetState()
        onClose()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const isBusy = isSubmitting || isBatchReverting

  const handleClose = () => {
    if (isBusy) return
    resetState()
    onClose()
  }

  if (!group) return null

  const firstItem = group.items[0]
  const temHistoricoSolicitante = !!(
    firstItem?.justificativaSolicitante || firstItem?.observacaoSolicitante
  )
  const temHistoricoAprovador = !!(
    firstItem?.justificativaAprovador || firstItem?.observacaoAprovador
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!isBusy}
      size="6xl"
    >
      <ModalOverlay />
      <ModalContent maxH="90vh" bg="white">
        <ModalHeader>
          <Flex justify="space-between" align="center">
            <HStack>
              <Text>Visualizar Pedido</Text>
              <Badge colorScheme="blue" variant="subtle">
                {getShortPedidoId(group.idRequestSap)}
              </Badge>
            </HStack>
          </Flex>
        </ModalHeader>

        <ModalCloseButton isDisabled={isBusy} />

        <ModalBody overflowY="auto" maxH="70vh" bg="white">
          <HStack spacing={2} mb={2}>
            {group.aprovado === true && (
              <Badge variant="solid" colorScheme="green">
                Aprovado
              </Badge>
            )}
            {group.aprovado === false && (
              <Badge variant="solid" colorScheme="red">
                Rejeitado
              </Badge>
            )}
            {isPendente(group.aprovado) && (
              <Badge variant="solid" colorScheme="orange">
                Pendente
              </Badge>
            )}
            {group.hasAlteracoes && (
              <Badge variant="solid" colorScheme="orange">
                Com Alterações
              </Badge>
            )}
            <Badge variant="solid">
              {group.totalItems} {group.totalItems === 1 ? 'item' : 'itens'}
            </Badge>
          </HStack>

          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab>Detalhes do Pedido</Tab>
              <Tab>MRP</Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                <VStack spacing={4} align="stretch">
                  {/* Informações do Grupo */}
                  <Box
                    p={4}
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.800"
                    position="relative"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      bg: 'green.500',
                      borderRadius: '4px 0 0 4px'
                    }}
                  >
                    <VStack spacing={2} align="stretch">
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Solicitado em: {formatDate(group.dataSolicitacao)}
                        </Text>
                      </HStack>

                      <HStack justify="space-between" spacing={6}>
                        <Box
                          textAlign="center"
                          bg="blue.50"
                          p={4}
                          borderRadius="md"
                          flex="1"
                          mr={2}
                        >
                          <Text
                            fontSize="xs"
                            color="gray.600"
                            fontWeight="bold"
                            mb={1}
                          >
                            TOTAL RECOMENDADO
                          </Text>
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="blue.600"
                          >
                            {group.totalRecomendado.toLocaleString('pt-BR')}
                          </Text>
                        </Box>

                        <Box
                          textAlign="center"
                          bg="green.50"
                          p={4}
                          borderRadius="md"
                          flex="1"
                          mr={2}
                        >
                          <Text
                            fontSize="xs"
                            color="gray.600"
                            fontWeight="bold"
                            mb={1}
                          >
                            TOTAL SOLICITADO
                          </Text>
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="green.600"
                          >
                            {group.totalDesejado.toLocaleString('pt-BR')}
                          </Text>
                        </Box>

                        {group.percentualAlteracaoGeral !== 0 && (
                          <Box
                            textAlign="center"
                            bg={
                              group.percentualAlteracaoGeral > 0
                                ? 'green.50'
                                : 'red.50'
                            }
                            p={3}
                            borderRadius="md"
                            flex="1"
                            mr={2}
                          >
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              fontWeight="bold"
                              mb={1}
                            >
                              ALTERAÇÃO GERAL
                            </Text>
                            <Text
                              fontSize="lg"
                              fontWeight="bold"
                              color={
                                group.percentualAlteracaoGeral > 0
                                  ? 'green.600'
                                  : 'red.600'
                              }
                            >
                              {group.percentualAlteracaoGeral > 0 ? '+' : ''}
                              {group.percentualAlteracaoGeral.toFixed(1)}%
                            </Text>
                          </Box>
                        )}
                      </HStack>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Tabela de Itens */}
                  <Box bg="white">
                    <Text mb={4} fontWeight="bold" fontSize="lg">
                      Itens do Pedido
                    </Text>
                    <GroupTable key={group.idRequestSap} items={group.items} />
                  </Box>

                  {/* Pedido criado sem utilizar o Cronograma de Compras
                      (informativo: não é o motivo da aprovação) */}
                  {group.foraCronograma && (
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <AlertTitle fontSize="sm">
                          Pedido fora do Cronograma de Compras
                        </AlertTitle>
                        <AlertDescription fontSize="sm">
                          {group.justificativaForaCronograma ||
                            'Sem justificativa informada.'}
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}

                  {/* Justificativas e Observações */}
                  {(temHistoricoSolicitante || temHistoricoAprovador) && (
                    <>
                      <Divider />

                      <VStack spacing={4} align="stretch">
                        <Text fontWeight="bold" fontSize="lg">
                          Histórico de Aprovação
                        </Text>

                        {/* Justificativa/observação do solicitante */}
                        {firstItem?.justificativaSolicitante && (
                          <Alert status="info" variant="left-accent">
                            <AlertIcon />
                            <Box>
                              <AlertTitle>
                                Justificativa do Solicitante:
                              </AlertTitle>
                              <AlertDescription>
                                {firstItem.justificativaSolicitante}
                              </AlertDescription>
                            </Box>
                          </Alert>
                        )}

                        {firstItem?.observacaoSolicitante && (
                          <Alert status="info" variant="left-accent">
                            <AlertIcon />
                            <Box>
                              <AlertTitle>
                                Observação do Solicitante:
                              </AlertTitle>
                              <AlertDescription>
                                {firstItem.observacaoSolicitante}
                              </AlertDescription>
                            </Box>
                          </Alert>
                        )}

                        {/* Justificativa do Aprovador */}
                        {firstItem?.justificativaAprovador && (
                          <Alert
                            status={
                              group.aprovado === true ? 'success' : 'error'
                            }
                            variant="left-accent"
                          >
                            <AlertIcon />
                            <Box>
                              <AlertTitle>
                                Justificativa do Aprovador (
                                {group.aprovado === true
                                  ? 'Aprovação'
                                  : 'Rejeição'}
                                ):
                              </AlertTitle>
                              <AlertDescription>
                                {firstItem.justificativaAprovador === 'Recusado'
                                  ? 'Rejeitado'
                                  : firstItem.justificativaAprovador}
                              </AlertDescription>
                            </Box>
                          </Alert>
                        )}

                        {/* Observação do Aprovador */}
                        {firstItem?.observacaoAprovador && (
                          <Alert
                            status={
                              group.aprovado === true ? 'success' : 'error'
                            }
                            variant="left-accent"
                          >
                            <AlertIcon />
                            <Box>
                              <AlertTitle>Observação do Aprovador:</AlertTitle>
                              <AlertDescription>
                                {firstItem.observacaoAprovador}
                              </AlertDescription>
                            </Box>
                          </Alert>
                        )}

                        {/* Data da Ação do Aprovador */}
                        {firstItem?.dataAcaoAprovador && (
                          <Text
                            fontSize="sm"
                            color="gray.600"
                            textAlign="right"
                          >
                            <strong>
                              Data da{' '}
                              {group.aprovado === true
                                ? 'aprovação'
                                : 'rejeição'}
                              :
                            </strong>{' '}
                            {formatDate(firstItem.dataAcaoAprovador)}
                          </Text>
                        )}
                      </VStack>
                    </>
                  )}

                  {showApprovalForm && podeEditar && (
                    <>
                      <Divider />

                      {/* Botões de Aprovação */}
                      <VStack spacing={4} align="stretch">
                        <Text fontWeight="bold" fontSize="lg" color="blue.600">
                          Ações do Pedido
                        </Text>

                        <FormControl>
                          <FormLabel>Observação (opcional)</FormLabel>
                          <Textarea
                            value={observacao}
                            onChange={e => setObservacao(e.target.value)}
                            placeholder="Digite observações adicionais (opcional)..."
                            rows={3}
                            borderColor="gray.300"
                            isDisabled={isBusy}
                          />
                        </FormControl>

                        {temFalhas && (
                          <FalhasAlert
                            itens={itensFalhos}
                            isChecked={cienteDosFalhos}
                            onChange={setCienteDosFalhos}
                            isDisabled={isBusy}
                          />
                        )}

                        <HStack spacing={4} justify="flex-end">
                          <Button
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleApproval(false)}
                            isLoading={isSubmitting}
                            loadingText="Rejeitando..."
                            isDisabled={isBusy}
                          >
                            Rejeitar
                          </Button>
                          <Button
                            colorScheme="green"
                            onClick={() => handleApproval(true)}
                            isLoading={isSubmitting}
                            loadingText="Aprovando..."
                            isDisabled={
                              isBusy || (temFalhas && !cienteDosFalhos)
                            }
                          >
                            Aprovar
                          </Button>
                        </HStack>
                      </VStack>
                    </>
                  )}
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Text fontWeight="bold" fontSize="lg">
                      Dados MRP
                    </Text>
                    {podeEditar && (
                      <Button
                        size="sm"
                        colorScheme="orange"
                        variant="solid"
                        leftIcon={<FaSyncAlt />}
                        onClick={handleRequestBatchRevert}
                        isLoading={isBatchReverting}
                        loadingText="Revertendo..."
                        isDisabled={isBusy || loadingMrp || !mrpData.length}
                      >
                        Voltar recomendação original (Em Lote)
                      </Button>
                    )}
                  </Flex>
                  {loadingMrp ? (
                    <Text>Carregando dados MRP...</Text>
                  ) : mrpData.length > 0 ? (
                    <TableGrid
                      key={tableKey}
                      id="purchase-table"
                      data={mrpData}
                      headers={
                        isScheduled
                          ? mrpScheduledPurchaseHeader
                          : mrpPurchaseHeader
                      }
                      loading={loadingMrp}
                      pagination={{
                        actualPage: 1,
                        rows: mrpData.length,
                        totalPages: 1,
                        totalRows: mrpData.length,
                        skip: 0
                      }}
                      height={''}
                      entity="mrp-purchase"
                      context={{
                        onUpdateWanted:
                          podeEditar && !isBusy
                            ? handleUpdateMrpWanted
                            : undefined,
                        isVisualizarPedido: true, // Flag para identificar que é a tela de visualização
                        pedidoAprovado: group.aprovado // Status de aprovação do pedido
                      }}
                    />
                  ) : (
                    <Text>Nenhum dado MRP encontrado.</Text>
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={handleClose} isDisabled={isBusy}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>

      <AlertDialog
        isOpen={revertConfirm.isOpen}
        leastDestructiveRef={revertCancelRef}
        onClose={revertConfirm.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="white">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Voltar recomendação original
            </AlertDialogHeader>
            <AlertDialogBody>
              Todos os itens com quantidade desejada diferente da recomendada
              voltarão para a quantidade recomendada. Deseja continuar?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={revertCancelRef} onClick={revertConfirm.onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleBatchRevertToOriginal}
                ml={3}
              >
                Reverter
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  )
}

export default VisualizarModal
